import { useState, useRef } from 'react';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Star, Camera, StopCircle, Upload, Loader2, CheckCircle2 } from 'lucide-react';

const COMFORT_EMOJIS = [
  { value: 'very_happy', label: 'Loves it!', emoji: '😄' },
  { value: 'happy', label: 'Happy', emoji: '🙂' },
  { value: 'neutral', label: 'Okay', emoji: '😐' },
  { value: 'uncomfortable', label: 'Uneasy', emoji: '😟' },
  { value: 'upset', label: 'Upset', emoji: '😢' },
];

export { COMFORT_EMOJIS };

export function SessionVideoActions({
  session, sessionId, activeSkill, onFetchSession
}) {
  const [recording, setRecording] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadSkillName, setUploadSkillName] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadNotes, setUploadNotes] = useState('');
  const [savingRecording, setSavingRecording] = useState(false);
  const [liveStream, setLiveStream] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const videoPreviewRef = useRef(null);
  const liveVideoRef = useRef(null);

  // Attach stream to video when both available
  const attachStream = () => {
    if (liveVideoRef.current && liveStream) {
      liveVideoRef.current.srcObject = liveStream;
      liveVideoRef.current.play().catch(() => {});
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: true });
      setRecording(true);
      setLiveStream(stream);
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        setRecordedBlob(blob);
        setShowPreview(true);
        stream.getTracks().forEach(t => t.stop());
        setLiveStream(null);
      };
      mediaRecorder.start();
      toast.success('Recording started!');
      setTimeout(attachStream, 100);
    } catch {
      toast.error('Camera access denied. Please allow camera access.');
      setRecording(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const approveRecording = async () => {
    if (!recordedBlob || !activeSkill) return;
    setSavingRecording(true);
    const formData = new FormData();
    formData.append('file', recordedBlob, `recording_${Date.now()}.webm`);
    formData.append('session_id', sessionId);
    formData.append('child_id', session.child_id);
    formData.append('skill_id', activeSkill.skill_id);
    formData.append('skill_name', activeSkill.name);
    try {
      await api.post('/videos/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => { if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total)); },
      });
      toast.success('Video saved!');
      setShowPreview(false);
      setRecordedBlob(null);
      setUploadProgress(0);
    } catch { toast.error('Failed to upload video. Try a shorter clip or check your connection.'); }
    finally { setSavingRecording(false); }
  };

  const handleFileUpload = async () => {
    if (!uploadFile) return;
    setUploadingVideo(true);
    setUploadProgress(0);
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('session_id', sessionId);
    formData.append('child_id', session.child_id);
    const skillName = uploadSkillName.trim() || activeSkill?.name || 'General';
    const matchedSkill = (session.skills || []).find(s => s.name === skillName);
    formData.append('skill_id', matchedSkill?.skill_id || activeSkill?.skill_id || '');
    formData.append('skill_name', skillName);
    formData.append('notes', uploadNotes.trim());
    try {
      await api.post('/videos/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => { if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total)); },
      });
      toast.success('Video uploaded!');
      setShowUpload(false);
      setUploadFile(null);
      setUploadSkillName('');
      setUploadProgress(0);
      setUploadNotes('');
    } catch { toast.error('Upload failed. Try a smaller file or check your connection.'); }
    finally { setUploadingVideo(false); }
  };

  const completeSkill = async () => {
    if (!activeSkill) return;
    try {
      await api.put(`/sessions/${sessionId}/skills/${activeSkill.skill_id}/complete`);
      toast.success(`${activeSkill.name} completed!`);
      onFetchSession();
    } catch { toast.error('Failed to complete skill'); }
  };

  return (
    <>
      <Card className="rounded-3xl border-slate-100 shadow-sm" data-testid="video-actions">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito' }}>
            Recording: {activeSkill?.name}
          </h3>
          {recording && (
            <div className="relative mb-4 rounded-2xl overflow-hidden bg-black" data-testid="live-preview">
              <video ref={liveVideoRef} autoPlay muted playsInline className="w-full aspect-video object-cover" />
              <div className="absolute top-3 left-3 flex items-center gap-2 bg-red-600/90 backdrop-blur-sm text-white px-3 py-1.5 rounded-full">
                <div className="recording-dot" />
                <span className="text-xs font-bold">RECORDING</span>
              </div>
            </div>
          )}
          <div className="space-y-3">
            {!recording ? (
              <Button data-testid="start-recording-btn" onClick={startRecording} className="w-full h-16 rounded-2xl bg-red-500 hover:bg-red-600 text-white font-bold text-lg gap-3">
                <Camera className="w-6 h-6" /> Start Recording
              </Button>
            ) : (
              <Button data-testid="stop-recording-btn" onClick={stopRecording} className="w-full h-16 rounded-2xl bg-red-600 hover:bg-red-700 text-white font-bold text-lg gap-3">
                <StopCircle className="w-6 h-6" /> Stop Recording
              </Button>
            )}
            <Button data-testid="upload-video-btn" variant="outline" onClick={() => setShowUpload(true)} className="w-full h-12 rounded-2xl font-bold gap-2">
              <Upload className="w-5 h-5" /> Upload Video
            </Button>
          </div>
          {activeSkill && !activeSkill.completed && (
            <Button data-testid="complete-skill-btn" onClick={completeSkill} className="w-full h-14 rounded-2xl bg-[#C4F017] hover:bg-[#A3D013] text-slate-800 font-bold text-lg gap-2 mt-4">
              <Star className="w-6 h-6" /> Complete & Add Star!
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Video Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="rounded-3xl max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Nunito' }}>Review Recording</DialogTitle></DialogHeader>
          {recordedBlob && (
            <div className="space-y-4">
              <video ref={videoPreviewRef} src={URL.createObjectURL(recordedBlob)} controls className="w-full rounded-2xl" />
              {savingRecording && (
                <div className="space-y-2">
                  <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                    <div className="bg-green-500 h-3 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                  </div>
                  <p className="text-xs text-center text-slate-500 font-semibold">{uploadProgress < 100 ? `Uploading... ${uploadProgress}%` : 'Processing...'}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Button data-testid="discard-recording-btn" variant="outline" onClick={() => { setRecordedBlob(null); setShowPreview(false); }} disabled={savingRecording} className="rounded-xl font-bold">Discard</Button>
                <Button data-testid="approve-recording-btn" onClick={approveRecording} disabled={savingRecording} className="rounded-xl bg-green-500 hover:bg-green-600 text-white font-bold gap-2">
                  {savingRecording ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><CheckCircle2 className="w-4 h-4" /> Approve & Save</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={(v) => { setShowUpload(v); if (!v) { setUploadFile(null); setUploadSkillName(''); } }}>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Nunito' }}>Upload Video</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <label className="text-sm font-semibold text-slate-600 mb-1.5 block">Skill / Caption</label>
              {(session?.skills || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {session.skills.map(s => (
                    <button key={s.skill_id} onClick={() => setUploadSkillName(s.name)} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${uploadSkillName === s.name ? 'bg-[#22D3EE] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid={`upload-select-skill-${s.skill_id}`}>
                      {s.name}
                    </button>
                  ))}
                </div>
              )}
              <input type="text" value={uploadSkillName} onChange={e => setUploadSkillName(e.target.value)} placeholder={activeSkill ? activeSkill.name : 'Or type a custom caption...'} className="w-full px-3 py-2 border rounded-xl text-sm focus:ring-2 focus:ring-[#22D3EE] focus:border-transparent outline-none" data-testid="upload-skill-caption-input" />
            </div>
            <input type="file" accept="video/*" onChange={e => setUploadFile(e.target.files[0] || null)} className="block w-full text-sm text-slate-500 file:mr-4 file:py-3 file:px-6 file:rounded-full file:border-0 file:text-sm file:font-bold file:bg-[#ECFEFF] file:text-[#22D3EE] hover:file:bg-[#BAE6FD] cursor-pointer" data-testid="video-file-input" />
            {uploadFile && <p className="text-xs text-slate-400">{uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(1)} MB)</p>}
            <div>
              <label className="text-sm font-semibold text-slate-600 mb-1 block">Notes (optional)</label>
              <textarea
                value={uploadNotes}
                onChange={e => setUploadNotes(e.target.value)}
                placeholder="e.g., Picked up 6 rings without help!"
                className="w-full px-3 py-2 border rounded-xl text-sm focus:ring-2 focus:ring-[#22D3EE] focus:border-transparent outline-none resize-none"
                rows={2}
                data-testid="upload-notes-input"
              />
            </div>
            {uploadingVideo && (
              <div className="space-y-2">
                <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                  <div className="bg-[#22D3EE] h-3 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                </div>
                <p className="text-xs text-center text-slate-500 font-semibold">{uploadProgress < 100 ? `Uploading... ${uploadProgress}%` : 'Processing...'}</p>
              </div>
            )}
            <button onClick={handleFileUpload} disabled={!uploadFile || uploadingVideo} className="w-full py-2.5 rounded-xl font-bold text-sm bg-[#1E1B4B] text-white hover:bg-[#2D2B66] disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2" data-testid="confirm-upload-video-btn">
              {uploadingVideo ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</> : 'Upload Video'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function SessionComfortRating({ session, sessionId, activeSkill, comfortRatings, onComfortRate }) {
  if (!activeSkill) return null;

  return (
    <Card className="rounded-3xl border-slate-100 shadow-sm" data-testid="comfort-rating">
      <CardContent className="p-6">
        <h3 className="text-lg font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito' }}>
          How comfortable? ({activeSkill.name})
        </h3>
        <div className="flex justify-center gap-3 flex-wrap">
          {COMFORT_EMOJIS.map(e => (
            <button key={e.value} onClick={() => onComfortRate(activeSkill.skill_id, e.value)} className={`emoji-btn flex flex-col items-center gap-1 p-3 rounded-2xl ${comfortRatings[activeSkill.skill_id] === e.value ? 'selected bg-[#ECFEFF]' : 'bg-slate-50 hover:bg-slate-100'}`} data-testid={`comfort-${e.value}`}>
              <span className="text-3xl">{e.emoji}</span>
              <span className="text-[10px] font-bold text-slate-500">{e.label}</span>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
